import * as models from "../../../lib/api/models";
import cn from "../../../lib/classnames";
import CollegeInput from "../../CollegeInput";
import NonInteractiveLinedInput from "../../NonInteractiveLinedInput";
import React, {useRef, useState} from "react";
import Select from "../../Select";
import styles from "./ExpandableCollege.module.css";
import useFocusInvalid from "../../../lib/useFocusInvalid";

const strings = {
    studentStatus: "Current Student Status",
    hsEntering: "High School / Entering College",
    collegeStudent: "College Student",
    none: "Not a Student",
    firstChoiceCollege: "First Choice College You Plan to Attend",
    collegeName: "College Name",
};

const studentOptions = [strings.hsEntering, strings.collegeStudent, strings.none];

const studentValues = [
    models.StudentStatus.HSEntering,
    models.StudentStatus.CollegeStudent,
    models.StudentStatus.NotAStudent,
];

type Props = {
    college: string;
    firstInvalidKey?: string;
    onBlurInput: (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onChangeApplication: (
        name: models.AppKeys | models.AddressKeys,
        value: string | boolean,
    ) => void;
    studentStatus: string | null;
    validationCollege?: string[];
    validationCount: number;
    validationStudentStatus?: string[];
};

export default function ExpandableCollege(props: Props) {
    const statusRef = useRef<HTMLSelectElement | null>(null);
    const collegeRef = useRef<HTMLInputElement | null>(null);
    const prevSchoolHasBeenClearedRef = useRef(false);

    const [prevSchoolStatus, setPrevSchoolStatus] = useState(props.studentStatus);
    const [prevSchool, setPrevSchool] = useState(props.college);

    const isOrInboundCollegeStudent =
        !!props.studentStatus && props.studentStatus !== models.StudentStatus.NotAStudent;

    let invalidRef;
    if (props.firstInvalidKey === models.AppKeys.StudentStatus) {
        invalidRef = statusRef;
    } else if (props.firstInvalidKey === models.AppKeys.College) {
        invalidRef = collegeRef;
    }

    useFocusInvalid(props.validationCount, invalidRef?.current);

    return (
        <>
            <Select
                id={models.AppKeys.StudentStatus}
                name={models.AppKeys.StudentStatus}
                label={strings.studentStatus}
                ref={statusRef}
                onBlur={props.onBlurInput}
                options={studentOptions}
                selectedIndex={studentValues.findIndex((v) => v === props.studentStatus)}
                setSelected={(index) => {
                    if (prevSchoolHasBeenClearedRef.current) {
                        prevSchoolHasBeenClearedRef.current = false;
                        setPrevSchool("");
                    }
                    setPrevSchoolStatus(props.studentStatus);
                    props.onChangeApplication(models.AppKeys.StudentStatus, studentValues[index]);

                    // If Not A Student selected, do not submit college attending value
                    if (studentValues[index] === models.StudentStatus.NotAStudent) {
                        setPrevSchool(props.college);
                        props.onChangeApplication(models.AppKeys.College, "");
                        prevSchoolHasBeenClearedRef.current = true;
                    }
                }}
                required={true}
                validationMessage={props.validationStudentStatus}
            />

            <div
                className={cn(
                    styles.content,
                    isOrInboundCollegeStudent ? styles.expanded : styles.collapsed,
                )}
            >
                {isOrInboundCollegeStudent ? (
                    <CollegeInput
                        id={models.AppKeys.College}
                        name={models.AppKeys.College}
                        onCollegeSelected={(collegeName) => {
                            props.onChangeApplication(models.AppKeys.College, collegeName);
                        }}
                        onBlur={props.onBlurInput}
                        label={
                            props.studentStatus === models.StudentStatus.HSEntering
                                ? strings.firstChoiceCollege
                                : strings.collegeName
                        }
                        ref={collegeRef}
                        required={true}
                        validationMessage={props.validationCollege}
                        initialSearch={props.college}
                    />
                ) : (
                    <NonInteractiveLinedInput
                        value={prevSchool}
                        label={
                            prevSchoolStatus === models.StudentStatus.HSEntering
                                ? strings.firstChoiceCollege
                                : strings.collegeName
                        }
                        isValid={prevSchool ? true : false}
                    />
                )}
            </div>
        </>
    );
}
